import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { CalendarDays, Clock, Loader2, MapPin } from "lucide-react";
import { ApiError } from "@eduviaos/shared";
import { Button, cn } from "@eduviaos/ui-kit";
import { Reveal, SectionCard, SectionHeader } from "../../components/Section";
import { apiErrorMessage } from "../../lib/apiErrorCopy";
import { intlLocale } from "../../lib/i18n/intl";
import { EASE_OUT } from "../../lib/motion";
import type { PortalLocale } from "../../lib/i18n/portalShell";
import type { PortalStrings } from "../../lib/i18n/usePortalLocale";
import { usePortalLocale } from "../../lib/i18n/usePortalLocale";
import {
  canBookSessions,
  clockTime,
  dayLabel,
  defaultBookingDateKey,
  slotDisplayName,
  slotTimeRange,
  useCreateBooking,
  useEffectiveSeatSlots,
  useMyFloorPlan,
  useSeatAvailability,
  useSeatRealtime,
  type MySeat,
  type SeatMode,
  type ZoneAvailabilityItem,
} from "../../lib/seat/api";
import { SeatFloorMap } from "./SeatFloorMap";
import { SeatMapGrid } from "./SeatMapGrid";

function seatBookingErrorMessage(
  err: unknown,
  locale: PortalLocale,
  strings: PortalStrings,
): string {
  if (!(err instanceof ApiError)) return strings.bookingFailedGeneric;
  const code = err.details?.code;
  if (typeof code === "string") {
    switch (code) {
      case "seat.zone_at_capacity":
        return strings.zoneFullNoWaitlist;
      case "seat.seat_already_booked":
        return strings.seatAlreadyTaken;
      case "seat.rebook_disabled":
        return strings.alreadyBookedThisDay;
      case "seat.claim_not_student_picks":
      case "seat.booking_disabled_in_none_mode":
        return strings.bookingNotAllowed;
      case "seat.student_already_assigned":
        return strings.alreadyHavePermanentSeat;
      case "seat.custom_overlaps":
        return strings.customOverlaps;
      case "seat.custom_duration_disabled":
      case "seat.custom_rate_missing":
        return strings.customDisabled;
      case "seat.booking_not_supported_in_fixed_mode":
        return strings.fixedRoomNotBookable;
      case "seat.outside_fixed_hours":
      case "seat.plan_requires_custom_hours":
        return strings.outsidePlanHours;
      case "seat.plan_zone_mismatch":
      case "seat.plan_seat_mismatch":
        return strings.bookingNotAllowed;
      default:
        break;
    }
  }
  if (err.status === 422) return strings.bookingNotAllowed;
  return apiErrorMessage(err, locale, strings, strings.bookingFailedGeneric);
}

function roomMode(zone: ZoneAvailabilityItem, centre: SeatMode): SeatMode {
  if (zone.effective_mode) return zone.effective_mode;
  if (zone.assignment_mode && zone.assignment_mode !== "inherit") return zone.assignment_mode;
  return centre;
}

function formatInr(paise: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(paise / 100);
}

export function SeatBookingFlow({ seat }: { seat: MySeat }) {
  const { strings, locale } = usePortalLocale();
  const reduce = useReducedMotion();
  const [dateKey, setDateKey] = React.useState(defaultBookingDateKey);
  const [chosenSlotKey, setChosenSlotKey] = React.useState<string | null>(null);
  const [zoneId, setZoneId] = React.useState<string | null>(null);
  const [selectedSeatId, setSelectedSeatId] = React.useState<string | null>(null);
  const [bookError, setBookError] = React.useState<string | null>(null);
  const [booked, setBooked] = React.useState(false);
  const [bookingKind, setBookingKind] = React.useState<"shift" | "hours">("shift");
  const [startTime, setStartTime] = React.useState("10:00");
  const [hours, setHours] = React.useState(2);

  const bookable = canBookSessions(seat);
  const plan = seat.plan ?? null;
  // A flexible-hours plan always books by the hour, even at a ₹0 rate.
  const flexiblePlan = plan?.kind === "flexible_hours";
  // Fixed hours as a daily window: the BE fills the window in, so the
  // learner only picks the day (and the seat when the plan has none).
  const windowPlan =
    plan?.kind === "fixed_hours" && Boolean(plan.window_start && plan.window_end) ? plan : null;
  const planShiftKeys =
    plan?.kind === "fixed_hours" && !windowPlan && plan.slot_keys.length > 0
      ? plan.slot_keys.join(",")
      : "";
  const planSeatId = plan?.kind === "fixed_hours" ? plan.seat_id : null;
  const planZoneId = plan?.kind === "zone_only" ? plan.zone_id : null;

  const rate = seat.custom_hourly_rate_paise ?? 0;
  const customAllowed =
    !windowPlan && (flexiblePlan || Boolean(seat.custom_duration_enabled && rate > 0));
  const byTheHour = customAllowed && (flexiblePlan || bookingKind === "hours");
  const realtime = useSeatRealtime(bookable);
  const floorPlanQuery = useMyFloorPlan(bookable && seat.mode === "dynamic");
  const slotsQuery = useEffectiveSeatSlots(dateKey, bookable);
  const slots = React.useMemo(() => {
    const all = slotsQuery.data ?? [];
    if (!planShiftKeys) return all;
    const allowed = new Set(planShiftKeys.split(","));
    return all.filter((s) => allowed.has(s.key));
  }, [slotsQuery.data, planShiftKeys]);
  const selectedSlot = slots.find((s) => s.key === chosenSlotKey) ?? slots[0] ?? null;
  const inventorySlot = byTheHour || windowPlan ? (slots[0] ?? null) : selectedSlot;
  const slotKey = inventorySlot?.key ?? "";
  const chargePaise = hours * rate;

  const availability = useSeatAvailability(
    dateKey,
    slotKey,
    bookable,
    realtime === "live" ? false : 15_000,
  );
  const createBooking = useCreateBooking();

  const zones = availability.data?.zones ?? [];
  const seats = availability.data?.seats ?? [];
  const zoneNames = React.useMemo(
    () => new Map((availability.data?.zones ?? []).map((z) => [z.zone_id, z.zone_name])),
    [availability.data?.zones],
  );
  const bookableZones = zones.filter((z) => {
    if (planZoneId) return z.zone_id === planZoneId;
    const mode = roomMode(z, seat.mode);
    return mode !== "fixed" && mode !== "none";
  });
  const hasPermanent = zones.some((z) => roomMode(z, seat.mode) === "fixed");
  const fixedZoneIds = new Set(
    zones.filter((z) => roomMode(z, seat.mode) === "fixed").map((z) => z.zone_id),
  );
  const openSeats = seats.filter(
    (s) => !fixedZoneIds.has(s.zone_id) && (!planSeatId || s.seat_id === planSeatId),
  );
  const filteredSeats =
    seat.mode === "dynamic" && zoneId ? openSeats.filter((s) => s.zone_id === zoneId) : openSeats;
  const mapSeats = byTheHour
    ? filteredSeats.map((s) => ({ ...s, available: true }))
    : filteredSeats;
  const floorPlan = floorPlanQuery.data ?? null;

  React.useEffect(() => {
    setZoneId(planZoneId);
    setSelectedSeatId(null);
    setBookError(null);
  }, [dateKey, slotKey, planZoneId]);

  React.useEffect(() => {
    setSelectedSeatId(null);
  }, [zoneId]);

  // A plan that names the seat leaves nothing to pick.
  const planSeatAvailable = planSeatId
    ? seats.some((s) => s.seat_id === planSeatId && (s.available || byTheHour))
    : false;
  React.useEffect(() => {
    if (planSeatId && planSeatAvailable) setSelectedSeatId(planSeatId);
  }, [planSeatId, planSeatAvailable, dateKey, slotKey]);

  const selectedZone = bookableZones.find((z) => z.zone_id === zoneId) ?? null;
  const startOk = /^([01]\d|2[0-3]):[0-5]\d$/.test(startTime);
  const canSubmit = byTheHour
    ? Boolean(
        startOk &&
        hours >= 1 &&
        hours <= 12 &&
        (seat.mode === "zone_only"
          ? zoneId && selectedZone && !selectedZone.is_full
          : selectedSeatId && mapSeats.some((s) => s.seat_id === selectedSeatId)),
      )
    : seat.mode === "zone_only"
      ? Boolean(zoneId && selectedZone && !selectedZone.is_full)
      : Boolean(
          selectedSeatId && filteredSeats.some((s) => s.seat_id === selectedSeatId && s.available),
        );

  // The seat map is long on phones, so the confirm button would otherwise
  // appear below the fold — behind the floating bottom nav.
  const bookButtonRef = React.useRef<HTMLDivElement>(null);
  const pickedId = seat.mode === "zone_only" ? zoneId : selectedSeatId;
  React.useEffect(() => {
    if (!canSubmit || !pickedId) return;
    bookButtonRef.current?.scrollIntoView?.({
      block: "nearest",
      behavior: reduce ? "auto" : "smooth",
    });
  }, [canSubmit, pickedId, reduce]);

  function handleBook() {
    setBookError(null);
    const windowFields = byTheHour
      ? { duration_minutes: hours * 60, custom_start: `${startTime}:00`, slot_key: "custom" }
      : { slot_key: slotKey };
    const body =
      seat.mode === "zone_only"
        ? { zone_id: zoneId!, date_key: dateKey, ...windowFields }
        : { seat_id: selectedSeatId!, date_key: dateKey, ...windowFields };

    createBooking.mutate(body, {
      onSuccess: () => setBooked(true),
      onError: (err) => setBookError(seatBookingErrorMessage(err, locale, strings)),
    });
  }

  if (booked) {
    return (
      <Reveal index={4}>
        <SectionHeader title={strings.bookASeat} />
        <SectionCard className="flex flex-col items-center gap-3 py-8 text-center">
          <span className="bg-primary/12 text-primary grid size-14 place-items-center rounded-2xl">
            <CalendarDays className="size-7" />
          </span>
          <div className="space-y-1">
            <p className="font-display text-lg font-semibold">{strings.bookingConfirmedTitle}</p>
            <p className="text-muted-foreground mx-auto max-w-xs text-sm">
              {strings.bookingConfirmedBody(
                dayLabel(dateKey, intlLocale(locale)),
                byTheHour
                  ? `${hours}h · ${startTime}${chargePaise > 0 ? ` · ${formatInr(chargePaise)}` : ""}`
                  : windowPlan
                    ? `${clockTime(windowPlan.window_start!)} – ${clockTime(windowPlan.window_end!)}`
                    : selectedSlot
                      ? slotDisplayName(selectedSlot, locale)
                      : slotKey,
              )}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              createBooking.reset();
              setSelectedSeatId(null);
              setBooked(false);
            }}
            className="mt-1 h-10 rounded-xl font-semibold transition-transform duration-150 ease-out active:scale-[0.97]"
          >
            {strings.bookAnotherSeat}
          </Button>
        </SectionCard>
      </Reveal>
    );
  }

  return (
    <Reveal index={4}>
      <SectionHeader title={strings.bookASeat} />
      <SectionCard className="space-y-5">
        <div className="space-y-2">
          <label
            htmlFor="seat-date"
            className="text-muted-foreground flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide"
          >
            <CalendarDays className="size-3.5" />
            {strings.pickDate}
          </label>
          <input
            id="seat-date"
            type="date"
            value={dateKey}
            min={defaultBookingDateKey()}
            onChange={(e) => setDateKey(e.target.value)}
            className="border-border/60 bg-background h-11 w-full rounded-xl border px-3 text-sm font-medium"
          />
        </div>

        {customAllowed && !flexiblePlan ? (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              aria-pressed={!byTheHour}
              onClick={() => setBookingKind("shift")}
              className={cn(
                "duration-120 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ease-out active:scale-[0.97]",
                !byTheHour
                  ? "border-primary bg-primary/12 text-primary"
                  : "border-border/60 hover:bg-muted/40",
              )}
            >
              {strings.namedShift}
            </button>
            <button
              type="button"
              aria-pressed={byTheHour}
              onClick={() => setBookingKind("hours")}
              className={cn(
                "duration-120 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ease-out active:scale-[0.97]",
                byTheHour
                  ? "border-primary bg-primary/12 text-primary"
                  : "border-border/60 hover:bg-muted/40",
              )}
            >
              {strings.bookByTheHour}
            </button>
          </div>
        ) : null}

        {byTheHour ? (
          <div className="grid grid-cols-2 gap-3">
            <label className="grid gap-1.5 text-sm">
              <span className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                {strings.customStart}
              </span>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="border-border/60 bg-background h-11 rounded-xl border px-3 text-sm font-medium tabular-nums"
              />
            </label>
            <label className="grid gap-1.5 text-sm">
              <span className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                {strings.customHours}
              </span>
              <input
                type="number"
                min={1}
                max={12}
                step={1}
                value={hours}
                onChange={(e) => setHours(Number(e.target.value))}
                className="border-border/60 bg-background h-11 rounded-xl border px-3 text-sm font-medium tabular-nums"
              />
            </label>
            {rate > 0 ? (
              <p
                className="text-foreground col-span-2 text-sm font-semibold tabular-nums"
                data-testid="seat-hours-charge"
              >
                {strings.customCharge(formatInr(chargePaise))}
              </p>
            ) : null}
          </div>
        ) : null}

        {windowPlan ? (
          <p className="text-muted-foreground flex items-center gap-1.5 text-sm">
            <Clock className="size-4" />
            {clockTime(windowPlan.window_start!)} – {clockTime(windowPlan.window_end!)}
          </p>
        ) : null}

        {!byTheHour && !windowPlan ? (
          <div className="space-y-2">
            <p className="text-muted-foreground flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
              <Clock className="size-3.5" />
              {strings.pickShift}
            </p>
            {slotsQuery.isLoading && (
              <div className="flex flex-wrap gap-2" aria-hidden="true">
                {[0, 1].map((i) => (
                  <span
                    key={i}
                    className="bg-muted/60 h-12 w-32 animate-pulse rounded-xl motion-reduce:animate-none"
                  />
                ))}
              </div>
            )}
            {slotsQuery.isError && (
              <div className="flex flex-col items-center gap-2 py-2 text-center">
                <p role="alert" className="text-destructive text-sm font-medium">
                  {strings.shiftsLoadFailed}
                </p>
                <Button variant="outline" size="sm" onClick={() => void slotsQuery.refetch()}>
                  {strings.retry}
                </Button>
              </div>
            )}
            {slotsQuery.isSuccess && slots.length === 0 && (
              <p className="text-muted-foreground border-border/60 rounded-xl border border-dashed px-4 py-3 text-sm">
                {strings.noShiftsOnDay}
              </p>
            )}
            {slots.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {slots.map((slot) => {
                  const active = selectedSlot?.key === slot.key;
                  return (
                    <button
                      key={slot.key}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setChosenSlotKey(slot.key)}
                      className={cn(
                        "duration-120 flex flex-col items-start rounded-xl border px-3 py-1.5 text-left transition-colors ease-out active:scale-[0.97]",
                        active
                          ? "border-primary bg-primary/12 text-primary"
                          : "border-border/60 hover:bg-muted/40",
                      )}
                    >
                      <span className="text-sm font-semibold">{slotDisplayName(slot, locale)}</span>
                      <span
                        className={cn(
                          "text-xs tabular-nums",
                          active ? "text-primary/80" : "text-muted-foreground",
                        )}
                      >
                        {slotTimeRange(slot)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}

        {availability.isLoading && (
          <div className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-sm">
            <Loader2 className="size-4 animate-spin" />
            {strings.loadingAvailability}
          </div>
        )}

        {availability.isError && (
          <div className="flex flex-col items-center gap-2 text-center">
            <p role="alert" className="text-destructive text-sm font-medium">
              {strings.availabilityLoadFailed}
            </p>
            <Button variant="outline" size="sm" onClick={() => void availability.refetch()}>
              {strings.retry}
            </Button>
          </div>
        )}

        {hasPermanent ? (
          <p className="text-muted-foreground text-sm">{strings.permanentRoomsNote}</p>
        ) : null}

        {availability.data && seat.mode === "zone_only" && (
          <ZonePicker
            zones={bookableZones}
            selectedId={zoneId}
            onSelect={setZoneId}
            strings={strings}
          />
        )}

        {availability.data && seat.mode === "dynamic" && bookableZones.length > 1 && (
          <div className="space-y-2">
            <p className="text-muted-foreground flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
              <MapPin className="size-3.5" />
              {strings.pickZoneOptional}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                aria-pressed={zoneId === null}
                onClick={() => setZoneId(null)}
                className={cn(
                  "duration-120 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ease-out active:scale-[0.97]",
                  zoneId === null
                    ? "border-primary bg-primary/12 text-primary"
                    : "border-border/60 hover:bg-muted/40",
                )}
              >
                {strings.allZones}
              </button>
              {bookableZones.map((z) => (
                <button
                  key={z.zone_id}
                  type="button"
                  aria-pressed={zoneId === z.zone_id}
                  onClick={() => setZoneId(z.zone_id)}
                  className={cn(
                    "duration-120 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ease-out active:scale-[0.97]",
                    zoneId === z.zone_id
                      ? "border-primary bg-primary/12 text-primary"
                      : "border-border/60 hover:bg-muted/40",
                  )}
                >
                  {z.zone_name}
                </button>
              ))}
            </div>
          </div>
        )}

        {availability.data && seat.mode === "dynamic" && floorPlan && (
          <SeatFloorMap
            plan={floorPlan}
            seats={mapSeats}
            selectedId={selectedSeatId}
            onSelect={setSelectedSeatId}
            realtime={realtime}
            strings={strings}
          />
        )}

        {availability.data && seat.mode === "dynamic" && !floorPlan && mapSeats.length > 0 && (
          <SeatMapGrid
            seats={mapSeats}
            selectedId={selectedSeatId}
            onSelect={setSelectedSeatId}
            zoneNames={zoneNames}
            strings={{
              seatTaken: strings.seatTaken,
              seatAvailable: strings.seatAvailable,
              yourSeat: strings.yourSeatLabel,
            }}
          />
        )}

        <AnimatePresence mode="wait">
          {canSubmit && (
            <motion.div
              key="book-btn"
              ref={bookButtonRef}
              className="scroll-mb-32"
              initial={reduce ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, y: -6 }}
              transition={{ duration: reduce ? 0 : 0.2, ease: EASE_OUT }}
            >
              <Button
                onClick={handleBook}
                disabled={createBooking.isPending}
                className="h-11 w-full rounded-xl font-semibold transition-transform duration-150 ease-out active:scale-[0.97]"
              >
                {createBooking.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    {seat.mode === "zone_only"
                      ? strings.bookingZonePending
                      : strings.bookingSeatPending}
                  </>
                ) : seat.mode === "zone_only" ? (
                  strings.bookZone
                ) : (
                  strings.bookSeat
                )}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {bookError && (
          <p role="alert" className="text-destructive text-center text-xs font-medium">
            {bookError}
          </p>
        )}
      </SectionCard>
    </Reveal>
  );
}

function ZonePicker({
  zones,
  selectedId,
  onSelect,
  strings,
}: {
  zones: ZoneAvailabilityItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  strings: PortalStrings;
}) {
  if (zones.length === 0) {
    return <p className="text-muted-foreground text-center text-sm">{strings.noZonesAvailable}</p>;
  }

  return (
    <div className="space-y-2">
      <p className="text-muted-foreground flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
        <MapPin className="size-3.5" />
        {strings.pickZone}
      </p>
      <div className="divide-border/60 border-border/60 divide-y rounded-xl border">
        {zones.map((zone) => {
          const selected = selectedId === zone.zone_id;
          const disabled = zone.is_full && !zone.waitlist_available;

          return (
            <button
              key={zone.zone_id}
              type="button"
              disabled={disabled}
              aria-pressed={selected}
              onClick={() => onSelect(zone.zone_id)}
              className={cn(
                "duration-120 flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors ease-out",
                selected && "bg-primary/8",
                !selected && !disabled && "hover:bg-muted/40 active:bg-muted/60",
                disabled && "cursor-not-allowed opacity-60",
              )}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold">{zone.zone_name}</p>
                <p className="text-muted-foreground text-xs">
                  {zone.is_full
                    ? zone.waitlist_available
                      ? strings.zoneFullWaitlist
                      : strings.zoneFullNoWaitlist
                    : strings.zoneSpotsLeft(zone.available, zone.capacity)}
                </p>
              </div>
              {selected && (
                <span className="bg-primary/12 text-primary shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold">
                  {strings.selected}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
